import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Modal } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  ArrowDown,
  ArrowUp,
  CornerDownLeft,
  Search,
  SearchX,
} from "lucide-react"
import { searchMenuItems } from "./sidebarMenu"

const LISTBOX_ID = "command-palette-results"
const optionId = path => `command-palette-option-${path.slice(1)}`

/**
 * Quick page search (Ctrl/⌘+K). Searches the sidebar menu, so it always
 * lists exactly the pages the sidebar does.
 */
const CommandPalette = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)

  const results = useMemo(() => searchMenuItems(query), [query])
  const active = results[activeIndex]

  // Fresh search each time it opens
  useEffect(() => {
    if (isOpen) {
      setQuery("")
      setActiveIndex(0)
    }
  }, [isOpen])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Keep the highlighted row visible while arrowing through a long list
  useEffect(() => {
    if (!active) return
    const row = listRef.current?.querySelector(`#${optionId(active.path)}`)
    if (row?.scrollIntoView) row.scrollIntoView({ block: "nearest" })
  }, [active])

  const go = item => {
    if (!item) return
    onClose()
    navigate(item.path)
  }

  const handleKeyDown = event => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setActiveIndex(i => (results.length ? (i + 1) % results.length : 0))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setActiveIndex(i =>
        results.length ? (i - 1 + results.length) % results.length : 0,
      )
    } else if (event.key === "Enter") {
      event.preventDefault()
      go(active)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      onOpened={() => inputRef.current?.focus()}
      autoFocus={false}
      className="command-palette"
      contentClassName="command-palette__content"
      labelledBy="command-palette-title"
    >
      <h2 id="command-palette-title" className="visually-hidden">
        Search pages
      </h2>

      <div className="command-palette__search">
        <Search size={18} strokeWidth={2} aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          className="command-palette__input"
          placeholder="Search pages…"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls={LISTBOX_ID}
          aria-activedescendant={active ? optionId(active.path) : undefined}
          aria-autocomplete="list"
          aria-label="Search pages"
          autoComplete="off"
          spellCheck={false}
        />
        <kbd className="command-palette__kbd">Esc</kbd>
      </div>

      {results.length === 0 ? (
        <div className="command-palette__empty" role="status">
          <SearchX size={20} strokeWidth={1.9} aria-hidden="true" />
          <span>No pages match “{query.trim()}”</span>
        </div>
      ) : (
        <ul
          id={LISTBOX_ID}
          ref={listRef}
          className="command-palette__list"
          role="listbox"
          aria-label="Pages"
        >
          {results.map((item, index) => {
            const Icon = item.icon
            const isActive = index === activeIndex
            return (
              <li
                key={item.path}
                id={optionId(item.path)}
                role="option"
                aria-selected={isActive}
                className={`command-palette__option${isActive ? " is-active" : ""}`}
                onMouseMove={() => !isActive && setActiveIndex(index)}
                onMouseDown={event => event.preventDefault()}
                onClick={() => go(item)}
              >
                <span className="command-palette__option-icon">
                  <Icon size={16} strokeWidth={1.9} aria-hidden="true" />
                </span>
                <span className="command-palette__option-label">
                  {t(item.label)}
                </span>
                <span className="command-palette__option-group">
                  {t(item.group)}
                </span>
              </li>
            )
          })}
        </ul>
      )}

      <div className="command-palette__footer" aria-hidden="true">
        <span>
          <kbd className="command-palette__kbd">
            <ArrowUp size={11} strokeWidth={2.4} />
          </kbd>
          <kbd className="command-palette__kbd">
            <ArrowDown size={11} strokeWidth={2.4} />
          </kbd>
          to move
        </span>
        <span>
          <kbd className="command-palette__kbd">
            <CornerDownLeft size={11} strokeWidth={2.4} />
          </kbd>
          to open
        </span>
      </div>
    </Modal>
  )
}

CommandPalette.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default CommandPalette
